/* eslint-disable */
import React from "react";

import Modal from "../../../../../components/ui/Modal";
import { MailIcon, MobileIcon, SuccessIcon } from "../../../../../assets/Icons";
import clsx from "clsx";
import ErrorField from "../../../../../components/ui/ErrorField";
import Button from "../../../../../components/ui/Button";
import CopyLink from "../../../../../assets/svg/CopyIcon.svg";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { remittanceApi } from "../../../api/remittanceApi";
import { toast } from "react-toastify";
import { AppStrings, logApiErrorToSentry } from "../../../utils";
import { remittanceActions } from "../../../state/remittanceSlice";
import { styles } from "./styles";

export interface RemitterEsignModalProps {
  visible?: boolean;
  onClose: () => void;
  modalTitle?: string;
  handleA2Form?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  a2Link?:string;
}

export const RemitterEsignModal: React.FC<RemitterEsignModalProps> = ({
  visible,
  onClose,
  modalTitle,
  handleA2Form,
  a2Link,
}) => {
  
  const dispatch = useAppDispatch();
  const { remitter } = useAppSelector((state) => state.remittance);

  const [mobileActive, setMobileActive] = React.useState(false);
  const [mailActive, setMailActive] = React.useState(false);
  const [step, setStep] = React.useState("1");
  const [error, setError] = React.useState("");
  const [isLinkedCopied, setIsLinkedCopied] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState({ mobileLoader: false, emailLoader: false });
  const [isTimeUp, setIsTimeUp] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(60);
  const [clickedTime, setClickedTime] = React.useState(0);

  const vendorUserKey = remitter?.selectedRemitter?.userKey ?? "";

  const resetSelections = () => {
    setMobileActive(false);
    setMailActive(false);
    setIsDisabled({ mobileLoader: false, emailLoader: false });
  };

  const startTimer = () => {
    clickedTime < 2 ? setTimeLeft(60) : timeLeft === 0 && setTimeLeft(60);
    setIsTimeUp(false);
    setClickedTime((p) => p + 1);
  };

  React.useEffect(() => {
    let interval: string | number | NodeJS.Timeout | undefined;
    if (visible && step === "2") {
      interval = setInterval(() => {
        remittanceApi.getRemitterSignedStatus(vendorUserKey)
          .then((res) => res?.data?.signed && setStep("3"))
          .catch((err) => {
            setError(err?.response?.data?.error?.type);
            logApiErrorToSentry(err, "getRemitterSignedStatus");
          });
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [visible, step]);

  React.useEffect(() => {
    if (timeLeft && clickedTime >= 2) {
      const t = setInterval(() => {
        setTimeLeft((prev) => prev <= 1 ? (setIsTimeUp(true), 0) : prev - 1);
      }, 1000);
      return () => clearInterval(t);
    }
  }, [timeLeft, clickedTime]);

  const sendLink = (type: string) => {
    startTimer();
    type === "sms" ? setMobileActive(true) : setMailActive(true);
    setIsDisabled((prev) => ({ ...prev, [type === "sms" ? "mobileLoader" : "emailLoader"]: true }));

    remittanceApi.sendRemitterEsignLinkToSms({
      sendType: type === "sms" ? "sms" : "Mail",
      userId: vendorUserKey,
      transactionNumber: null,
      resend: false,
    })
      .then(() => setStep("2"))
      .catch((err) => {
        setError(err?.response?.data?.error?.type);
        setTimeout(() => setError(""), 3000);
        logApiErrorToSentry(err, "sendRemitterEsignLinkToSms");
      })
      .finally(() =>
        setIsDisabled((p) => ({ ...p, [type === "sms" ? "mobileLoader" : "emailLoader"]: false }))
      );
  };

  const copyLink = () => {
    if (!a2Link) return;
    setIsLinkedCopied(true);
    navigator.clipboard.writeText(a2Link);
    setTimeout(() => setIsLinkedCopied(false), 3000);
  };

  const formatTime = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2,"0")}.${String(sec % 60).padStart(2,"0")}`;

  const handleDoLaterA2Form = () => {
    remittanceApi.remitterChangeStatusLater(vendorUserKey)
      .then(() => {
        onClose();
        dispatch(remittanceActions.fetchRemitterSignedStatusRequested(vendorUserKey));
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR)
        logApiErrorToSentry(err, "fetchRemitterSignedStatusRequested");
      });
  };

  return (
    <Modal
      modalClass={styles.modalClass}
      headerClass={styles.headerClass}
      visible={visible}
      header={modalTitle || AppStrings.REMITTER_ESIGN_MODAL.SIGN_A2_FORM}
      closeHandler={() => { onClose(); resetSelections(); }}
    >

      {step === "1" && (
        <div className={styles.receiveLinkWrapper}>
          
          <div className="flex w-full justify-center items-center">
            <h1 className={styles.receiveLinkTitle}>{AppStrings.REMITTER_ESIGN_MODAL.RECEIVE_LINK_ON}</h1>
            
            <button onClick={() => a2Link && copyLink()} className="flex justify-center items-center pl-4">
              <span className={`${styles.copyLinkText} ${isLinkedCopied ? "text-green-500" : "text-[#0F5BF1]"}`}>
                {isLinkedCopied ? AppStrings.REMITTER_ESIGN_MODAL.LINK_COPIED : AppStrings.REMITTER_ESIGN_MODAL.COPY_LINK}
              </span>
              <img className="pl-2" src={CopyLink} alt="copy-link" />
            </button>
          </div>

          {(mobileActive || mailActive) && 
            <span className={styles.linkActiveNote}>{AppStrings.REMITTER_ESIGN_MODAL.LINK_ACTIVE_FOR_5_MIN}</span>
          }

          <div className="flex items-center justify-center w-full py-4">

            <Button
              isLoading={isDisabled.mobileLoader}
              onClickHandler={() => sendLink("sms")}
              className={clsx(styles.sendOptionButton)}
              bgColor={mobileActive ? "bg-[#282A30] text-white" : "bg-[#F1F4FF]"}
            >
              <MobileIcon />
              <h1 className="pl-3 text-[20px]">{AppStrings.REMITTER_ESIGN_MODAL.MOBILE}</h1>
            </Button>

            <Button
              isLoading={isDisabled.emailLoader}
              onClickHandler={() => sendLink("mail")}
              className={clsx(styles.sendOptionButton)}
              bgColor={mailActive ? "bg-[#282A30] text-white" : "bg-[#F1F4FF]"}
            >
              <MailIcon />
              <h1 className="pl-3 text-[20px]">{AppStrings.REMITTER_ESIGN_MODAL.EMAIL}</h1>
            </Button>

          </div>

          {(mobileActive || mailActive) && (
            <span className={styles.linkSentMessage}>
              {mobileActive
                ? AppStrings.REMITTER_ESIGN_MODAL.LINK_SENT_MSG
                : AppStrings.REMITTER_ESIGN_MODAL.LINK_SENT_EMAIL}
            </span>
          )}

          <div onClick={handleDoLaterA2Form} className="mt-10 flex flex-col items-center cursor-pointer">
            <h1 className="text-primary font-semibold text-[20px]">{AppStrings.REMITTER_ESIGN_MODAL.DO_IT_LATER}</h1>
            <ErrorField errorMessage={error} />
          </div>
        </div>
      )}

      {step === "2" && (
        <div className={styles.awaitingWrapper}>

          <div className="w-2/5 mb-10">
            <h1 className={styles.awaitingTitle}>
              {mobileActive
                ? AppStrings.REMITTER_ESIGN_MODAL.LINK_SENT_MSG
                : AppStrings.REMITTER_ESIGN_MODAL.LINK_SENT_EMAIL}
            </h1>
          </div>

          <div className="mb-10">
            <h1 className={styles.awaitingNote}>{AppStrings.REMITTER_ESIGN_MODAL.NOTE_DRAW_SIGNATURE}</h1>
          </div>

          <div className="mb-5">
            <h1 className={styles.awaitingNote}>{AppStrings.REMITTER_ESIGN_MODAL.AWAITING_SIGNATURE}</h1>
          </div>

          <div className={styles.dotsWrapper}>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
          </div>

          {clickedTime > 2 && !isTimeUp && (
            <span className="font-semibold text-[12.2px] text-center">{formatTime(timeLeft)}</span>
          )}

          <Button
            onClickHandler={() => { setStep("1"); resetSelections(); }}
            disabled={clickedTime > 2 && !isTimeUp}
            isLoading={mobileActive ? isDisabled.mobileLoader : isDisabled.emailLoader}
            bgColor={clickedTime > 2 && !isTimeUp ? "bg-gray-600" : "bg-primary"}
            className={styles.resendBtn}
          >
            {AppStrings.REMITTER_ESIGN_MODAL.RESEND_LINK}
          </Button>

        </div>
      )}

      {step === "3" && (
        <div className={styles.signedWrapper}>

          <h1 className={styles.signedTitle}>{AppStrings.REMITTER_ESIGN_MODAL.FORM_SIGNED}</h1>
          <h1 className={styles.signedSubtitle}>{AppStrings.REMITTER_ESIGN_MODAL.THANKS}</h1>

          <SuccessIcon className="mb-5" />

          <h1
            onClick={handleA2Form}
            className={styles.signedLink}
          >
            {AppStrings.REMITTER_ESIGN_MODAL.DOWNLOAD_A2}
          </h1>

          <h1
            onClick={() => { onClose(); setStep("1"); }}
            className={styles.signedLink}
          >
            {AppStrings.REMITTER_ESIGN_MODAL.GO_BACK}
          </h1>

        </div>
      )}

    </Modal>
  );
};
